import React, { useMemo } from 'react';
import { ClickablePhoto, PhotoLightbox, usePhotoLightbox } from './PhotoLightbox';
import './NamslBaltimoreTuskersRecapArticle.css';
import './PhotoGallery.css';

const AWARD_LABELS = {
  MVP: 'Most Valuable Player',
  BEST_GOALKEEPER: 'Best Goalkeeper',
  BEST_FORWARD: 'Best Forward',
  BEST_DEFENDER: 'Best Defender',
};

const AWARD_ICONS = {
  MVP: '🏆',
  BEST_GOALKEEPER: '🧤',
  BEST_FORWARD: '⚽',
  BEST_DEFENDER: '🛡️',
};

function sortMembersByStats(members, playerStats) {
  const sortedStats = [...(playerStats || [])].sort(
    (a, b) => b.goals - a.goals || b.assists - a.assists
  );
  const statRank = new Map(sortedStats.map((stat, index) => [stat.playerId, index]));

  return [...members].sort((a, b) => {
    const rankA = statRank.has(a.playerId) ? statRank.get(a.playerId) : Number.MAX_SAFE_INTEGER;
    const rankB = statRank.has(b.playerId) ? statRank.get(b.playerId) : Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return (a.playerName || '').localeCompare(b.playerName || '');
  });
}

export function TournamentRecapTeamPhotos({ photos, copy = {} }) {
  const {
    eyebrow = 'Tournament photos',
    title = 'The squad',
    intro = 'Team photos from the tournament weekend. Tap any photo to enlarge.',
    altPrefix = 'Team photo',
  } = copy;

  const items = useMemo(
    () => (photos || []).map((url, index) => ({
      src: url,
      alt: `${altPrefix} ${index + 1}`,
      caption: `Team photo ${index + 1}`,
    })),
    [photos, altPrefix]
  );
  const lightbox = usePhotoLightbox(items);

  if (!photos?.length) return null;

  return (
    <section className="tuskers-recap__team-photos" aria-label="Team photos">
      <div className="tuskers-recap__section-head">
        <p className="tuskers-recap__eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="tuskers-recap__section-intro">{intro}</p>
      </div>
      <div className={`tuskers-recap__team-grid tuskers-recap__team-grid--${Math.min(photos.length, 2)}`}>
        {items.map((item, index) => (
          <figure key={item.src} className="tuskers-recap__team-shot">
            <ClickablePhoto
              src={item.src}
              alt={item.alt}
              loading={index === 0 ? 'eager' : 'lazy'}
              onClick={() => lightbox.openAt(index)}
            />
          </figure>
        ))}
      </div>
      <PhotoLightbox
        items={items}
        index={lightbox.index}
        onClose={lightbox.close}
        onPrev={lightbox.goPrev}
        onNext={lightbox.goNext}
      />
    </section>
  );
}

export function TournamentRecapSquad({ members, playerStats, copy = {} }) {
  const {
    eyebrow = 'Meet the team',
    title = 'Tournament roster',
    intro = 'The players who represented the club — with tournament goals and assists where they contributed on the scoreboard. Tap a player photo to enlarge.',
  } = copy;

  const sorted = useMemo(
    () => sortMembersByStats(members, playerStats),
    [members, playerStats]
  );
  const items = useMemo(
    () => sorted.map((member) => ({
      src: member.photoUrl,
      alt: member.playerName,
      caption: member.playerName?.trim(),
    })),
    [sorted]
  );
  const lightbox = usePhotoLightbox(items);
  const statByPlayer = new Map((playerStats || []).map((s) => [s.playerId, s]));

  if (!members?.length) return null;

  return (
    <section className="tuskers-recap__squad" aria-label="Tournament squad">
      <div className="tuskers-recap__section-head">
        <p className="tuskers-recap__eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="tuskers-recap__section-intro">{intro}</p>
      </div>
      <div className="tuskers-recap__squad-grid">
        {sorted.map((member, index) => {
          const stat = statByPlayer.get(member.playerId);
          const goals = stat?.goals ?? 0;
          const assists = stat?.assists ?? 0;
          const hasContributions = goals > 0 || assists > 0;

          return (
            <article key={member.playerId} className="tuskers-recap__player-card">
              <div className="tuskers-recap__player-photo-wrap">
                <ClickablePhoto
                  src={member.photoUrl}
                  alt={member.playerName}
                  onClick={() => lightbox.openAt(index)}
                />
              </div>
              <div className="tuskers-recap__player-info">
                <h3>{member.playerName?.trim()}</h3>
                {hasContributions ? (
                  <p className="tuskers-recap__player-statline">
                    {goals > 0 && <span>{goals} goal{goals !== 1 ? 's' : ''}</span>}
                    {goals > 0 && assists > 0 && <span className="tuskers-recap__stat-dot">·</span>}
                    {assists > 0 && <span>{assists} assist{assists !== 1 ? 's' : ''}</span>}
                  </p>
                ) : (
                  <p className="tuskers-recap__player-statline tuskers-recap__player-statline--muted">
                    Squad member
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <PhotoLightbox
        items={items}
        index={lightbox.index}
        onClose={lightbox.close}
        onPrev={lightbox.goPrev}
        onNext={lightbox.goNext}
      />
    </section>
  );
}

export function TournamentRecapAwards({ awards, copy = {} }) {
  const {
    eyebrow = 'Tournament honors',
    title = 'Individual awards',
    intro = 'Standout performers recognized from the tournament weekend. Tap a photo to enlarge.',
  } = copy;

  const visible = (awards || []).filter((a) => a.playerName || a.photoUrl);
  const photoItems = useMemo(
    () => visible
      .filter((award) => award.photoUrl)
      .map((award) => ({
        src: award.photoUrl,
        alt: award.playerName || AWARD_LABELS[award.type],
        caption: [
          AWARD_LABELS[award.type] || award.type,
          award.playerName,
        ].filter(Boolean).join(' — '),
      })),
    [visible]
  );
  const lightbox = usePhotoLightbox(photoItems);
  const photoIndexByType = useMemo(() => {
    const map = new Map();
    photoItems.forEach((item, index) => {
      const award = visible.filter((a) => a.photoUrl)[index];
      if (award) map.set(award.type, index);
    });
    return map;
  }, [photoItems, visible]);

  if (!visible.length) return null;

  return (
    <section className="tuskers-recap__awards" aria-label="Tournament awards">
      <div className="tuskers-recap__section-head">
        <p className="tuskers-recap__eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="tuskers-recap__section-intro">{intro}</p>
      </div>
      <div className="tuskers-recap__awards-grid">
        {visible.map((award) => (
          <article key={award.type} className="tuskers-recap__award-card">
            <div className="tuskers-recap__award-badge">
              <span aria-hidden="true">{AWARD_ICONS[award.type] || '🏅'}</span>
              <span>{AWARD_LABELS[award.type] || award.type}</span>
            </div>
            {award.photoUrl ? (
              <div className="tuskers-recap__award-photo">
                <ClickablePhoto
                  src={award.photoUrl}
                  alt={award.playerName || AWARD_LABELS[award.type]}
                  onClick={() => lightbox.openAt(photoIndexByType.get(award.type) ?? 0)}
                />
              </div>
            ) : (
              <div className="tuskers-recap__award-photo tuskers-recap__award-photo--placeholder">
                <span>{AWARD_ICONS[award.type] || '🏅'}</span>
              </div>
            )}
            {award.playerName && (
              <h3 className="tuskers-recap__award-name">{award.playerName}</h3>
            )}
          </article>
        ))}
      </div>
      <PhotoLightbox
        items={photoItems}
        index={lightbox.index}
        onClose={lightbox.close}
        onPrev={lightbox.goPrev}
        onNext={lightbox.goNext}
      />
    </section>
  );
}

export function TournamentRecapPhotosLoading() {
  return (
    <section className="tuskers-recap__photos-loading wellness-article__panel" aria-live="polite">
      <p>Loading tournament photos and awards…</p>
    </section>
  );
}
